import React, { useEffect, useMemo, useState } from "react";
import { buildAuthedJsonBody, getUserFromToken } from "../../../../utils/functions.ts";
import { formatEasternDisplayDate } from "../../../../utils/dateUtils.ts";

const ENDPOINT = "https://www.fbxchange.com/ReactPHP/fileSection.php";

const formatFileDate = (rawDate) => {
    return formatEasternDisplayDate(rawDate);
};

const cleanFormatValue = (value) => {
    const raw = String(value ?? "-").toUpperCase();
    if (raw.includes("PDF")) return "PDF";
    if (raw.includes("/")) return raw.split("/").pop();
    return raw || "-";
};

const normalizeRow = (f) => ({
    ...f,
    id: f?.id ?? f?.ID ?? "",
    title: f?.fileDesc || f?.fileName || f?.originalName || "-",
    by: f?.fileBy || "-",
    format: cleanFormatValue(f?.format ?? f?.fileType ?? "-"),
    fileDateFormatted: formatFileDate(f?.uploadDate ?? f?.fileDate),
    fileUrl: f?.fileUrl || "",
    size: f?.fileSizeLabel || "",
});

const OverviewFiles = ({ FBXID }) => {
    const playerId = useMemo(() => {
        const n = Number(FBXID);
        return Number.isFinite(n) && n > 0 ? String(FBXID) : "";
    }, [FBXID]);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errMsg, setErrMsg] = useState("");
    const [selectedId, setSelectedId] = useState("");

    useEffect(() => {
        const controller = new AbortController();

        const fetchFiles = async () => {
            setRows([]);
            setErrMsg("");
            setSelectedId("");

            if (!playerId) return;

            setLoading(true);

            const token = await getUserFromToken().catch(() => null);
            if (!token?.team) {
                setLoading(false);
                setErrMsg("Missing user/team token.");
                return;
            }

            try {
                const res = await fetch(ENDPOINT, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    signal: controller.signal,
                    body: buildAuthedJsonBody({
                        scriptName: "getFiles",
                        scout: token.scout,
                        team: token.team,
                        FBXID: playerId,
                    }),
                });

                const raw = await res.text();
                let data;
                try {
                    data = JSON.parse(raw);
                } catch (e) {
                    setErrMsg("Backend did not return JSON.");
                    setLoading(false);
                    return;
                }

                if (!data?.success) {
                    setErrMsg(data?.error || "Failed to load files.");
                    setLoading(false);
                    return;
                }

                const playerFiles = Array.isArray(data?.playerFiles) ? data.playerFiles : [];
                const normalized = playerFiles.map(normalizeRow);
                setRows(normalized);
                setSelectedId(normalized[0]?.id ? String(normalized[0].id) : "");
                setLoading(false);
            } catch (e) {
                if (e?.name === "AbortError") return;
                setErrMsg("Network error loading files.");
                setLoading(false);
            }
        };

        fetchFiles();

        return () => controller.abort();
    }, [playerId]);

    const selectedFile = useMemo(() => {
        if (!rows.length) return null;
        return rows.find((f) => String(f.id) === String(selectedId)) || rows[0];
    }, [rows, selectedId]);

    const renderPreview = () => {
        if (!selectedFile) {
            return (
                <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted">
                    <em>No files found.</em>
                </div>
            );
        }

        if (!selectedFile.fileUrl) {
            return (
                <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted">
                    <em>No preview URL available.</em>
                </div>
            );
        }

        return (
            <iframe
                src={selectedFile.fileUrl}
                title={selectedFile.fileName || selectedFile.title || "PDF Preview"}
                style={{ width: "100%", height: "100%", border: "none" }}
            />
        );
    };

    return (
        <div className="container-fluid p-0 h-100">
            <div className="row g-0 h-100">
                <div className="col-48 h-100 d-flex flex-column">
                    <div
                        className="row g-1 bg-navy text-lemon fs-7 py-2 px-2 align-items-center"
                        style={{ position: "sticky", top: 0, zIndex: 25 }}
                    >
                        <div className="col-20">
                            <strong>Player Files</strong>
                        </div>
                        <div className="col-12 text-center">
                            {!loading && !errMsg ? <span>{rows.length}</span> : <span>-</span>}
                        </div>
                        <div className="col-16 text-end">
                            {selectedFile?.fileUrl ? (
                                <a
                                    href={selectedFile.fileUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn btn-sm btn-outline-light"
                                >
                                    Open PDF
                                </a>
                            ) : null}
                        </div>
                    </div>

                    <div className="row g-0 flex-grow-1" style={{ minHeight: 0 }}>
                        <div className="col-14 border-end h-100 d-flex flex-column" style={{ minHeight: 0 }}>
                            <div className="bg-primary text-white small fw-semibold px-2 py-1">
                                File Name
                            </div>
                            <div className="flex-grow-1" style={{ overflowY: "auto", minHeight: 0 }}>
                                {loading ? (
                                    <div className="p-2 text-muted fs-7">
                                        <em>Loading files...</em>
                                    </div>
                                ) : errMsg ? (
                                    <div className="alert alert-warning py-2 m-2 fs-7">{errMsg}</div>
                                ) : rows.length === 0 ? (
                                    <div className="p-2 text-muted small">
                                        <em>No files found.</em>
                                    </div>
                                ) : (
                                    rows.map((f) => {
                                        const active = String(f.id) === String(selectedFile?.id);
                                        return (
                                            <button
                                                key={f.id}
                                                type="button"
                                                className={`btn btn-sm w-100 text-start rounded-0 border-bottom ${active ? "btn-warning" : "btn-light"}`}
                                                onClick={() => setSelectedId(String(f.id))}
                                                title={f.title}
                                            >
                                                <div className="text-truncate fw-semibold">{f.title}</div>
                                                <div className="small text-muted text-truncate">
                                                    {f.fileDateFormatted} | {f.by}
                                                </div>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        <div className="col-34 h-100 d-flex flex-column" style={{ minHeight: 0 }}>
                            <div className="border-bottom px-2 py-2">
                                <div className="row g-2 align-items-end">
                                    <div className="col-24">
                                        <label className="form-label mb-1 small text-muted">Description</label>
                                        <input
                                            className="form-control form-control-sm"
                                            value={selectedFile?.title || ""}
                                            disabled
                                        />
                                    </div>
                                    <div className="col-8">
                                        <label className="form-label mb-1 small text-muted">Date</label>
                                        <input
                                            className="form-control form-control-sm"
                                            value={selectedFile?.fileDateFormatted || ""}
                                            disabled
                                        />
                                    </div>
                                    <div className="col-8">
                                        <label className="form-label mb-1 small text-muted">By</label>
                                        <input
                                            className="form-control form-control-sm"
                                            value={selectedFile?.by || ""}
                                            disabled
                                        />
                                    </div>
                                    <div className="col-4">
                                        <label className="form-label mb-1 small text-muted">Type</label>
                                        <input
                                            className="form-control form-control-sm"
                                            value={selectedFile?.format || ""}
                                            disabled
                                        />
                                    </div>
                                    <div className="col-4">
                                        <label className="form-label mb-1 small text-muted">Size</label>
                                        <input
                                            className="form-control form-control-sm"
                                            value={selectedFile?.size || ""}
                                            disabled
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex-grow-1 bg-white" style={{ minHeight: 0 }}>
                                {renderPreview()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OverviewFiles;
